import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Check } from 'lucide-react';
import { formatCredits, formatDollars } from '@/lib/format-credits';
import type { GrTierResponse } from '@/lib/api';

interface TierPricingCardProps {
  tier: GrTierResponse;
  currentTier?: string | null;
  onPurchase: (tier: GrTierResponse['tier']) => void;
  purchasing?: boolean;
}

/**
 * One Lit Pass tier for sale. The price is in Fias credits with its dollar
 * equivalent beside it (1 credit = $0.01); a tier the server reports with no
 * price is shown as unavailable rather than priced by guesswork.
 */
export function TierPricingCard({
  tier,
  currentTier,
  onPurchase,
  purchasing,
}: TierPricingCardProps) {
  const isCurrentTier = currentTier === tier.tier;
  const priceCredits = tier.priceCredits;
  const unavailable = priceCredits === null;

  return (
    <Card className={isCurrentTier ? 'border-primary' : undefined}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{tier.displayName}</CardTitle>
          {isCurrentTier && (
            <Badge variant="default" className="text-xs">
              <Check className="h-3 w-3 mr-1" />
              Current
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {unavailable ? (
          <p className="text-sm text-muted-foreground">Unavailable</p>
        ) : (
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold">{formatCredits(priceCredits)}</span>
            <span className="text-sm text-muted-foreground">
              credits ({formatDollars(priceCredits)})
            </span>
          </div>
        )}
        <p className="text-sm text-muted-foreground">
          {tier.durationDays} {tier.durationDays === 1 ? 'day' : 'days'} · up to{' '}
          {tier.rewardMultiplier}× rewards
        </p>
        {tier.description && <p className="text-sm text-muted-foreground">{tier.description}</p>}
        <Button
          className="w-full"
          onClick={() => onPurchase(tier.tier)}
          disabled={isCurrentTier || purchasing || unavailable}
        >
          {purchasing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {isCurrentTier ? 'Active' : 'Subscribe'}
        </Button>
      </CardContent>
    </Card>
  );
}
